import CrossFilledIcon from '@shared/assets/icons/cross/filled.svg';
import NotificationEnabledIcon from '@shared/assets/icons/notification/enabled.svg';
import PlusFilledIcon from '@shared/assets/icons/plus/filled.svg';
import { type MBImageURISource } from '@shared/components/image';
import { Colors } from '@shared/styles';
import { type API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';

const iconColorByMessageType: Partial<Record<API.EntityEventType, string>> = {
  CARD_CREATED: Colors.blue.light,
  CARD_UPDATED: Colors.blue.light,
  CARD_DELETED: Colors.red.primary,
  CARD_REMINDER_SENT: Colors.red.primary,
};

const iconByMessageType: Partial<Record<API.EntityEventType, MBImageURISource>> = {
  CARD_CREATED: PlusFilledIcon,
  CARD_UPDATED: PlusFilledIcon,
  CARD_DELETED: CrossFilledIcon,
  CARD_REMINDER_SENT: NotificationEnabledIcon,
};

export function getIconColor(message: API.ChatMessageFragment): string {
  return iconColorByMessageType[message.subType ?? ''] as string;
}

export function getIcon(message: API.ChatMessageFragment): MBImageURISource {
  return iconByMessageType[message.subType ?? ''] as MBImageURISource;
}

/*
  Card system messages ("CARD_CREATED"/"CARD_DELETED") arrive as server-rendered
  English content in the shape "{name} created a new {entity}" / "{name} removed
  {entity}". Only the verb phrase is translatable — the actor name and the
  "{template}: {card}" tail are user data we keep verbatim — so we swap just that
  phrase in place. Word order is identical in en/es/fr (all SVO), so this holds.
  Any other subtype (or unexpected wording) falls through to the original content.
*/
export function localizeCardSystemMessageContent(message: {
  subType?: API.MessageSubType | null;
  content: string;
}): string {
  if (message.subType === 'CARD_CREATED') {
    return message.content.replace(
      ' created a new ',
      ` ${i18n('chat.systemMessage.cardCreatedVerb')} `,
    );
  }
  if (message.subType === 'CARD_DELETED') {
    return message.content.replace(' removed ', ` ${i18n('chat.systemMessage.cardRemovedVerb')} `);
  }
  return message.content;
}
